import { useState } from 'react';
import { Loader, Spinner } from '@oliasoft-open-source/react-ui-library';
import { useTranslation } from 'react-i18next';
import translations from 'client/internationalization/translation-map.json';
import { Crud } from 'common/interfaces/access-rights';
import { INode, IObject } from 'common/interfaces/hierarchy-entities';
import { findNameClash, MoveClash } from 'common/utils/name-clash';
import {
  checkMoveClash as checkMoveClashAction,
  moveObjectToNode as moveObjectToNodeAction,
  removeDuplicateSource as removeDuplicateSourceAction,
} from 'client/store/entities/object/object';
import { MoveFileDialog } from './move-file-dialog';
import { ROOT_FOLDER_ID } from './move-targets';

enum MoveStep {
  Classification = 'classification',
  Checking = 'checking',
}

const CLASH_TEXTS: Partial<Record<MoveClash, { heading: string, message: string }>> = {
  [MoveClash.Identical]: { heading: translations.moveIdenticalHeading, message: translations.moveIdenticalMessage },
  [MoveClash.Different]: { heading: translations.moveDifferentHeading, message: translations.moveDifferentMessage },
  [MoveClash.Restricted]: { heading: translations.moveRestrictedHeading, message: translations.moveRestrictedMessage },
};

type PendingMove = {
  object: IObject;
  targetNodeId: string;
  targetName: string;
  targetClassification?: string;
  hasNameClash: boolean;
  step: MoveStep | MoveClash;
};

type UseMoveFileOptions = {
  nodes: INode[];
  objects: IObject[];
  rootParentNode?: Pick<INode, 'nodeid' | 'name'> & Partial<Pick<INode, 'classificationlevel'>>;
  moveObjectToNode: (...args: Parameters<typeof moveObjectToNodeAction>) => void;
  checkMoveClash: (...args: Parameters<typeof checkMoveClashAction>) => void;
  removeDuplicateSource: (...args: Parameters<typeof removeDuplicateSourceAction>) => void;
};

const toNamedItem = (object: IObject) => {
  return { id: object.objectid, name: object.name };
};

export const useMoveFile = ({
  nodes, objects, rootParentNode, moveObjectToNode, checkMoveClash, removeDuplicateSource,
}: UseMoveFileOptions) => {
  const { t } = useTranslation();
  const [pendingMove, setPendingMove] = useState<PendingMove>();

  const close = () => {
    setPendingMove(undefined);
  };

  const execute = (move: PendingMove, keepBoth?: boolean) => {
    const { object, targetNodeId, targetName } = move;
    moveObjectToNode({
      objectid: object.objectid,
      nodeid: targetNodeId,
      oldNodeid: object.nodeid,
      keepBoth,
      successMessage: t(translations.fileMoved, { name: object.name, catalogue: targetName }) as string,
    });
    close();
  };

  const removeSource = (move: PendingMove) => {
    const { object, targetNodeId } = move;
    removeDuplicateSource(object, targetNodeId);
    close();
  };

  const proceedAfterClassification = (move: PendingMove) => {
    if (!move.hasNameClash) {
      return execute(move);
    }
    setPendingMove({ ...move, step: MoveStep.Checking });
    checkMoveClash(
      { objectid: move.object.objectid, nodeid: move.targetNodeId },
      (clash) => {
        if (clash === MoveClash.None) {
          return execute(move);
        }
        setPendingMove({ ...move, step: clash });
      },
      close,
    );
  };

  const requestMove = (objectId: string, targetFolderId: string | number) => {
    const object = objects.find((entry) => entry.objectid === objectId);
    const targetNode = targetFolderId === ROOT_FOLDER_ID
      ? rootParentNode
      : nodes.find((node) => node.nodeid === String(targetFolderId));
    if (!object || !targetNode || targetNode.nodeid === object.nodeid) {
      return;
    }
    const targetNodeId = targetNode.nodeid;

    const objectsInTarget = objects.filter((entry) => entry.nodeid === targetNodeId);
    const move: PendingMove = {
      object,
      targetNodeId,
      targetName: targetNode.name,
      targetClassification: targetNode.classificationlevel,
      hasNameClash: !!findNameClash(toNamedItem(object), objectsInTarget.map(toNamedItem)),
      step: MoveStep.Classification,
    };
    const hasDifferentClassification = !!targetNode.classificationlevel && targetNode.classificationlevel !== object.classificationlevel;
    if (hasDifferentClassification) {
      return setPendingMove(move);
    }
    proceedAfterClassification(move);
  };

  const renderClassificationDialog = (move: PendingMove) => {
    const { object, targetName, targetClassification } = move;
    const level = t(translations[`classificationLevel_${targetClassification}` as keyof typeof translations]);
    return (
      <MoveFileDialog
        heading={t(translations.moveClassificationHeading)}
        message={t(translations.moveClassificationMessage, { name: object.name, catalogue: targetName, level })}
        actions={[
          { label: t(translations.move), colored: true, onClick: () => proceedAfterClassification(move) },
          { label: t(translations.cancel), onClick: close },
        ]}
        onClose={close}
      />
    );
  };

  const renderCheckingDialog = () => {
    return (
      <MoveFileDialog
        heading={t(translations.checkingFile)}
        message={<Loader height="50px" theme="light"><Spinner dark /></Loader>}
        actions={[]}
      />
    );
  };

  const renderClashDialog = (move: PendingMove, step: MoveClash) => {
    const { object, targetName } = move;
    const isIdentical = step === MoveClash.Identical;
    const hasDeleteRight = object.rights ? object.rights[Crud.DELETE] : true;
    const { heading, message } = CLASH_TEXTS[step]!;
    return (
      <MoveFileDialog
        heading={t(heading)}
        message={t(message, { name: object.name, catalogue: targetName })}
        actions={[
          ...(isIdentical
            ? [{ label: t(translations.removeSourceKeepExisting), colored: true, disabled: !hasDeleteRight, onClick: () => removeSource(move) }]
            : []),
          { label: t(translations.renameSource), colored: !isIdentical || !hasDeleteRight, onClick: () => execute(move, true) },
          { label: t(translations.cancel), onClick: close },
        ]}
        onClose={close}
      />
    );
  };

  const renderDialog = () => {
    if (!pendingMove) {
      return null;
    }
    if (pendingMove.step === MoveStep.Classification) {
      return renderClassificationDialog(pendingMove);
    }
    if (pendingMove.step === MoveStep.Checking) {
      return renderCheckingDialog();
    }
    return renderClashDialog(pendingMove, pendingMove.step);
  };

  return { requestMove, renderDialog };
};
